import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Upload, FileCheck, X, ChevronLeft, Sparkles, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import PrivacyBadge from '@/components/PrivacyBadge';
import { calculateBundlePrice } from '@/data/mockData';

const BundleUploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { visaType, documentCount } = location.state || {};
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const bundlePricing = documentCount ? calculateBundlePrice(documentCount) : null;
  const actualBundlePricing = selectedFiles.length > 0 ? calculateBundlePrice(selectedFiles.length) : null;

  useEffect(() => {
    if (!visaType || !documentCount) {
      navigate('/select-country');
    }
  }, [visaType, documentCount, navigate]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const validFiles = files.filter(file => validTypes.includes(file.type));
    
    if (validFiles.length !== files.length) {
      alert('Some files were skipped. Only PDF, JPG, and PNG files are supported.');
    }
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleAnalyze = () => {
    if (selectedFiles.length > 0) {
      navigate('/bundle-processing', { 
        state: { 
          fileCount: selectedFiles.length,
          files: selectedFiles.map(f => ({
            name: f.name,
            type: f.type,
            size: f.size
          }))
        } 
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/documents')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <PrivacyBadge variant="small" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12 md:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles className="w-8 h-8 text-trust" />
              <h1 className="font-outfit font-bold text-4xl md:text-5xl text-slate-900">Bundle Upload</h1>
            </div>
            <p className="font-public text-lg text-slate-600 mb-4">
              Upload multiple documents for {visaType} and save with bulk discount
            </p>
          </div>

          {/* Initial Pricing Info (before upload) */}
          {selectedFiles.length === 0 && bundlePricing && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
              <div className="text-center">
                <p className="font-public text-sm text-slate-600 mb-2">Bundle Pricing for {documentCount} documents</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl font-outfit font-bold text-trust">${bundlePricing.finalPrice}</span>
                  <span className="text-lg text-slate-400 line-through">${bundlePricing.totalWithoutDiscount}</span>
                  <span className="px-2 py-1 bg-verify text-verify-foreground rounded-md text-xs font-mono">Save ${bundlePricing.savings}</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">Price will adjust based on files uploaded</p>
              </div>
            </motion.div>
          )}

          {/* Actual Pricing (after upload) */}
          {selectedFiles.length > 0 && actualBundlePricing && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-r from-verify/10 to-trust/10 border-2 border-verify/30 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Tag className="w-8 h-8 text-verify" />
                  <div>
                    <p className="font-outfit font-bold text-2xl text-slate-900">
                      ${actualBundlePricing.finalPrice}
                      <span className="text-lg text-slate-500 line-through ml-2">${actualBundlePricing.totalWithoutDiscount}</span>
                    </p>
                    <p className="font-public text-sm text-slate-600">
                      for {actualBundlePricing.documentCount} document{actualBundlePricing.documentCount > 1 ? 's' : ''} • Save ${actualBundlePricing.savings} ({actualBundlePricing.discount}% off)
                    </p>
                  </div>
                </div>
                <div className="bg-verify text-verify-foreground px-4 py-2 rounded-md font-mono text-sm">
                  ${actualBundlePricing.perDocumentPrice}/doc
                </div>
              </div>
            </motion.div>
          )}

          <div className="mb-8"><PrivacyBadge /></div>

          <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm mb-6">
            <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} className={`border-2 border-dashed rounded-lg p-12 text-center transition-all ${dragActive ? 'border-trust bg-trust/5' : 'border-slate-300 hover:border-trust/50'}`} data-testid="upload-dropzone">
              <div className="bg-trust/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-trust" />
              </div>
              <h3 className="font-public font-semibold text-lg text-slate-900 mb-2">Drag and drop your files here</h3>
              <p className="font-public text-slate-600 mb-4">or</p>
              <label className="inline-block">
                <input type="file" onChange={handleFileInput} accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden" data-testid="file-input" />
                <span className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-medium hover:bg-trust/90 active:scale-95 transition-all cursor-pointer inline-block shadow-sm">Choose Files</span>
              </label>
              <p className="font-public text-sm text-slate-500 mt-4">Supported formats: PDF, JPG, PNG (Max 10MB per file)</p>
              <p className="font-public text-sm text-trust mt-2">Upload up to {documentCount} documents</p>
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-public font-semibold text-slate-900">Selected Files ({selectedFiles.length}/{documentCount})</h3>
                <button onClick={() => setSelectedFiles([])} className="text-sm text-slate-500 hover:text-destructive transition-colors">Clear All</button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200" data-testid={`file-item-${index}`}>
                    <div className="flex items-center gap-3 flex-1">
                      <FileCheck className="w-5 h-5 text-verify" />
                      <div className="flex-1 min-w-0">
                        <p className="font-public font-medium text-slate-900 truncate">{file.name}</p>
                        <p className="font-public text-xs text-slate-500">{file.type.split('/')[1].toUpperCase()} • {formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button onClick={() => removeFile(index)} className="p-2 hover:bg-slate-200 rounded-md transition-colors" data-testid={`remove-file-${index}`}>
                      <X className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {selectedFiles.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <button onClick={handleAnalyze} className="bg-trust text-trust-foreground px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust/90 active:scale-95 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2" data-testid="analyze-bundle-button">
                <Sparkles className="w-5 h-5" />
                Analyze {selectedFiles.length} Document{selectedFiles.length > 1 ? 's' : ''} - ${actualBundlePricing?.finalPrice}
              </button>
              <p className="text-sm text-slate-500 mt-3">You're saving ${actualBundlePricing?.savings} with bundle pricing!</p>
            </motion.div>
          )}

          <div className="mt-12 bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-public font-semibold text-slate-900 mb-3">Bundle Upload Benefits</h3>
            <ul className="space-y-2 font-public text-sm text-slate-600">
              <li className="flex items-start gap-2"><span className="text-verify font-semibold">✓</span><span>Automatically save 10-30% compared to single uploads</span></li>
              <li className="flex items-start gap-2"><span className="text-verify font-semibold">✓</span><span>Process all your visa documents at once</span></li>
              <li className="flex items-start gap-2"><span className="text-verify font-semibold">✓</span><span>Get comprehensive readiness report for your entire application</span></li>
              <li className="flex items-start gap-2"><span className="text-verify font-semibold">✓</span><span>Same privacy-first processing - all files deleted after analysis</span></li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BundleUploadPage;
